/** Renders an Action's title/when/where/needs box. Shared by PostCard and TranslationCard. */
import { useTranslation } from 'react-i18next'
import { Zap, Calendar, MapPin, Users, Package, Navigation, CircleArrowOutUpRight } from 'lucide-react'
import type { ActionPost, PostCategory } from '@olimhub/shared'
import { formatDateTime } from '@/lib/dates'
import { buildMapsUrl } from '@/lib/maps'
import { useIsDesktop } from '@/hooks'
import { CategoryChip } from './CategoryChip'

export function ActionMeta({ action, category, distanceKm }: { action: ActionPost['action']; category?: PostCategory | null; distanceKm?: number }) {
  const { t } = useTranslation()
  const isMobile = !useIsDesktop()
  const startFormatted = formatDateTime(action.startsAt, { autoHideTime: true })

  return (
    <div
      style={{
        margin: '12px 16px 0',
        padding: '12px 14px',
        background: 'color-mix(in srgb, var(--action) 7%, var(--surface))',
        border: '1px solid color-mix(in srgb, var(--action) 24%, var(--border))',
        borderRadius: 'var(--radius-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 6,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Zap size={17} color="var(--action)" strokeWidth={1.8} />
        <span
          style={{
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--fs-body)',
            fontWeight: 700,
            color: 'var(--text-strong)',
          }}
        >
          {action.title}
        </span>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Calendar size={17} color="var(--action)" strokeWidth={1.8} />
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-strong)', fontWeight: 600 }}>
          {startFormatted}
        </span>
      </div>

      {action.locationText && (isMobile ? (
        <a
          href={buildMapsUrl(action.locationText)}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t('search.open_in_maps')}
          onClick={(e) => e.stopPropagation()}
          style={{ display: 'flex', alignItems: 'center', gap: 7, textDecoration: 'none' }}
        >
          <MapPin size={17} color="var(--action)" strokeWidth={1.8} style={{ flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--action)', textDecoration: 'underline', textUnderlineOffset: 2 }}>
            {action.locationText}
          </span>
          <CircleArrowOutUpRight size={14} color="var(--action)" strokeWidth={1.8} style={{ flexShrink: 0 }} />
        </a>
      ) : (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <MapPin size={17} color="var(--action)" strokeWidth={1.8} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)' }}>
            {action.locationText}
          </span>
        </div>
      ))}

      <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
        <Users size={17} color="var(--action)" strokeWidth={1.8} />
        <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
          {t('actions.rsvp_count', { count: action.rsvpCount })}
        </span>
      </div>

      {action.needsText && (
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 7 }}>
          <Package size={17} color="var(--action)" strokeWidth={1.8} style={{ marginTop: 1, flexShrink: 0 }} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-body)' }}>
            {action.needsText}
          </span>
        </div>
      )}

      {distanceKm !== undefined && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <Navigation size={17} color="var(--action)" strokeWidth={1.8} />
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 'var(--fs-sm)', color: 'var(--text-muted)' }}>
            {t('search.location_distance', { km: distanceKm })}
          </span>
        </div>
      )}

      {category && (
        <div>
          <CategoryChip category={category} />
        </div>
      )}
    </div>
  )
}
